<script setup lang="ts">
import {computed, ref} from "vue";
import {useCrossFilterStore} from "@/stores/crossFilterStore.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import QuestionFieldsEditor from "@/components/questionFields/QuestionFieldsEditor.vue";
import CrossFilterRenderer from "@/components/bi/CrossFilterRenderer.ts";
import Translator from "@/utils/common/Translator.ts";
import QuestionField = namaTypes.QuestionField;

const props = defineProps<{
  widgetId: string;
  questionFields?: QuestionField[];
}>();

const emit = defineEmits<{
  filtersChanged: []
}>();

const crossFilterStore = useCrossFilterStore();
const presenter = new QuestionFieldsPresenter();
const dialogOpen = ref(false);

const activeCount = computed(() => crossFilterStore.localFilterCount(props.widgetId));

const activeFilterLines = computed(() => {
  const filters = crossFilterStore.getLocalFilters(props.widgetId);
  return filters.map(f => {
    const q = (props.questionFields ?? []).find(x => x.id === f.id);
    return {
      id: f.id ?? '',
      icon: CrossFilterRenderer.iconForType(q?.fieldType),
      label: CrossFilterRenderer.renderLabel(q),
      value: CrossFilterRenderer.renderValue(f, q),
    };
  });
});

async function openDialog() {
  if (!props.questionFields?.length) return;
  await presenter.edit(props.questionFields, crossFilterStore.getLocalFilters(props.widgetId));
  dialogOpen.value = true;
}

function onApplyFilters() {
  const filled = presenter.getQuestionFields();
  crossFilterStore.clearAllLocalFiltersFor(props.widgetId);
  for (const f of filled) {
    if (f.id && f.value?.trim())
      crossFilterStore.setLocalFilter(props.widgetId, f.id, f.value);
  }
  dialogOpen.value = false;
  emit('filtersChanged');
}

function onClearAll() {
  crossFilterStore.clearAllLocalFiltersFor(props.widgetId);
  dialogOpen.value = false;
  emit('filtersChanged');
}
</script>

<template>
  <q-btn v-if="questionFields?.length"
         dense flat round size="sm" icon="filter_list" color="primary"
         @click.stop="openDialog">
    <q-badge v-if="activeCount > 0" floating color="red">{{ activeCount }}</q-badge>
    <q-tooltip v-if="activeCount > 0" anchor="bottom middle" self="top middle"
               class="tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark tw-text-namaFontColor-light dark:tw-text-white tw-shadow-md tw-p-2">
      <div class="tw-text-xs tw-font-semibold tw-mb-1">{{ Translator.translate('biWidgetLocalFilters') }}</div>
      <div class="tw-flex tw-flex-col tw-gap-1">
        <div v-for="line in activeFilterLines" :key="line.id" class="tw-flex tw-items-center tw-gap-1">
          <q-icon v-if="line.icon" :name="line.icon" size="14px"/>
          <span v-if="line.label" class="tw-font-medium">{{ line.label }}:</span>
          <span>{{ line.value }}</span>
        </div>
      </div>
    </q-tooltip>
    <q-tooltip v-else>{{ Translator.translate('biWidgetLocalFilters') }}</q-tooltip>

    <q-dialog v-model="dialogOpen">
      <q-card class="tw-flex tw-flex-col tw-bg-namaBackground-bright-light dark:tw-text-white dark:tw-bg-namaBackground-bright-dark"
              style="min-width: 500px;">
        <q-bar class="tw-text-namaFontColor-light tw-bg-[#CED4DA] tw-px-2 tw-py-[6px] tw-flex tw-justify-between dark:tw-bg-[#212529] dark:tw-text-white">
          <div>{{ Translator.translate('biWidgetLocalFilters') }}</div>
          <q-btn dense flat icon="close" v-close-popup :title="Translator.translate('biClose')"/>
        </q-bar>
        <q-card-section>
          <QuestionFieldsEditor :presenter="presenter" :singleColumnGroup="true" :doNotApplyThemeLayouts="true"/>
        </q-card-section>
        <q-card-actions align="right" class="tw-px-3 tw-pb-3">
          <q-btn v-if="activeCount > 0" flat color="negative" :label="Translator.translate('biClearAll')" @click="onClearAll"/>
          <q-btn flat :label="Translator.translate('biCancel')" v-close-popup/>
          <q-btn color="primary" :label="Translator.translate('biApply')" @click="onApplyFilters"/>
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-btn>
</template>
